import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Archive, ArrowUpRight, Loader2, Search, X,
} from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { getPosts, siteSearch } from "../lib/api";
import {
  usePageContent, kickerStyle, titleStyle,
} from "../context/PageContentContext";
import CardIntro from "../components/CardIntro";
import { usePostTypes } from "../lib/postTypes";

const useDebounced = (value, delay = 350) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

export default function ArchivePage() {
  const copy = usePageContent("archive");
  const location = useLocation();
  const inputRef = useRef(null);
  const { label: postTypeLabel } = usePostTypes();

  // Auto-focus and scroll to the search field when arriving from the header search icon (#search anchor)
  useEffect(() => {
    if (location.hash === "#search") {
      const run = () => {
        const element = document.getElementById("search");
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
        if (inputRef.current) {
          inputRef.current.focus();
        }
      };
      run();
      const timerId = setTimeout(run, 100);
      return () => clearTimeout(timerId);
    }
  }, [location.hash, location.key]);

  // Auto-focus the search field when arriving from the header search icon (#search anchor).
  const [query, setQuery] = useState(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      return params.get("q") || "";
    }
    return "";
  });
  const debounced = useDebounced(query, 300);

  // Two view modes: 'archive' (browse every published post) or 'search' (search everything)
  const [archived, setArchived] = useState(null);   // [] | null while loading
  const [results, setResults] = useState(null);     // {posts, links} | null
  const [error, setError] = useState("");

  // Browse archived list when no query
  useEffect(() => {
    if (debounced) return; // search effect handles the typed case
    let alive = true;
    setError("");
    getPosts("archive")
      .then((data) => alive && setArchived(data))
      .catch((e) => alive && setError(e.message || "Couldn't load archive"));
    return () => {
      alive = false;
    };
  }, [debounced]);

  // Site-wide search when query is non-empty
  useEffect(() => {
    if (!debounced) {
      setResults(null);
      return;
    }
    let alive = true;
    setError("");
    setResults({ loading: true });
    siteSearch(debounced)
      .then((data) => alive && setResults(data))
      .catch((e) => alive && setError(e.message || "Search failed"));
    return () => {
      alive = false;
    };
  }, [debounced]);

  const inSearchMode = !!debounced;

  return (
    <div
      className="min-h-screen bg-[var(--page-bg)] text-[var(--brand-ink)]"
      data-testid="page-archive"
    >
      <Header />
      <main className="mx-auto max-w-5xl px-5 pt-4 pb-16 sm:px-8 sm:pt-10 sm:pb-20">
        {copy.hide_header ? null : (
          <div className="border-b border-black/10 pb-6">
            <div
              className="flex items-center gap-2 text-brand-accent"
              style={kickerStyle(copy)}
            >
              {inSearchMode ? <Search size={16} strokeWidth={2.5} /> : <Archive size={16} strokeWidth={2.5} />}
              <span className="label-kicker">
                {inSearchMode ? "Search" : copy.kicker}
              </span>
            </div>
            <h1
              className="brand-headline mt-3 whitespace-pre-line font-display text-4xl font-black tracking-tight sm:text-5xl"
              style={titleStyle(copy)}
            >
              {inSearchMode ? `Results for "${debounced}"` : copy.title}
            </h1>
            {!inSearchMode && copy.intro ? (
              <p className="mt-3 max-w-2xl text-base leading-relaxed text-[var(--brand-body)]">
                {copy.intro}
              </p>
            ) : null}
          </div>
        )}

        {/* Search bar */}
        <div className="mt-8" id="search">
          <label className="block">
            <span className="label-kicker text-[var(--brand-body)]">
              Search the whole site
            </span>
            <div className="mt-2 flex items-center gap-2 rounded-md border border-black/15 bg-white px-3 py-2 focus-within:border-[#0A0F17]">
              <Search size={16} className="text-[var(--brand-body)]" />
              <input
                ref={inputRef}
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search posts, link directory, opponent, season…"
                autoFocus
                data-testid="archive-search"
                className="block w-full bg-transparent font-display text-base outline-none placeholder:text-[#94A3B8]"
              />
              {query ? (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  data-testid="archive-search-clear"
                  className="text-[var(--brand-body)] hover:text-[var(--brand-ink)]"
                  aria-label="Clear search"
                >
                  <X size={16} />
                </button>
              ) : null}
            </div>
          </label>
        </div>

        {error ? (
          <p
            className="mt-6 rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-2 text-sm"
            data-testid="archive-error"
          >
            {error}
          </p>
        ) : null}

        {/* Results */}
        {inSearchMode ? (
          <SearchResults state={results} query={debounced} postTypeLabel={postTypeLabel} />
        ) : (
          <ArchiveList posts={archived} postTypeLabel={postTypeLabel} />
        )}
      </main>
      <Footer />
    </div>
  );
}

function ArchiveList({ posts, postTypeLabel }) {
  if (posts === null) {
    return (
      <div className="mt-10 flex items-center gap-2 text-[var(--brand-body)]">
        <Loader2 size={14} className="animate-spin" /> Loading…
      </div>
    );
  }
  if (posts.length === 0) {
    return (
      <div
        className="mt-10 rounded-md border border-dashed border-black/15 bg-[#F8FAFC] p-8 text-center text-sm text-[var(--brand-body)]"
        data-testid="archive-empty"
      >
        Nothing in the archive yet.
      </div>
    );
  }
  return (
    <ul className="mt-10 space-y-3" data-testid="archive-list">
      {posts.map((p) => (
        <PostRow key={p.id} post={p} postTypeLabel={postTypeLabel} />
      ))}
    </ul>
  );
}

function SearchResults({ state, query, postTypeLabel }) {
  if (!state || state.loading) {
    return (
      <div className="mt-10 flex items-center gap-2 text-[var(--brand-body)]">
        <Loader2 size={14} className="animate-spin" /> Searching…
      </div>
    );
  }

  const posts = state.posts || [];
  const total = posts.length;

  if (total === 0) {
    return (
      <div
        className="mt-10 rounded-md border border-dashed border-black/15 bg-[#F8FAFC] p-8 text-center text-sm text-[var(--brand-body)]"
        data-testid="search-empty"
      >
        No matches for &ldquo;{query}&rdquo;.
        <br />
        Try a shorter or differently-spelt term.
      </div>
    );
  }

  return (
    <div className="mt-10 space-y-12" data-testid="search-results">
      <div className="flex flex-wrap items-baseline gap-3">
        <span className="label-kicker text-[var(--brand-body)]">
          {total} {total === 1 ? "match" : "matches"}
        </span>
        <span className="label-kicker text-brand-accent">
          {posts.length} post{posts.length === 1 ? "" : "s"}
        </span>
      </div>

      {posts.length > 0 ? (
        <section data-testid="search-results-posts">
          <h2 className="font-display text-xl font-black tracking-tight">
            Posts
          </h2>
          <ul className="mt-4 space-y-3">
            {posts.map((p) => (
              <PostRow key={p.id} post={p} postTypeLabel={postTypeLabel} />
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

function PostRow({ post, postTypeLabel }) {
  return (
    <li>
      <div
        data-testid={`archive-row-${post.id}`}
        className="card-hover group relative flex flex-col gap-2 overflow-hidden rounded-md border border-black/10 bg-white p-5 transition-colors hover:border-[#0A0F17] sm:flex-row sm:items-center sm:justify-between"
      >
        {/* Stretched link so the whole row is clickable, but inline
            links inside the intro override this via `.card-intro a`
            (z-index in index.css). */}
        <Link
          to={`/post/${post.slug || post.id}`}
          aria-label={post.title}
          className="absolute inset-0 z-[1]"
          data-testid={`archive-row-link-${post.id}`}
        />
        <div className="pointer-events-none relative z-[2] flex min-w-0 items-center gap-4">
          {post.cover_image ? (
            <img
              src={post.cover_image}
              alt={post.title}
              loading="lazy"
              data-testid={`archive-row-thumb-${post.id}`}
              className="h-20 w-20 shrink-0 rounded-md object-cover sm:h-24 sm:w-24"
            />
          ) : null}
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="label-kicker text-brand-accent" data-testid={`archive-row-type-${post.id}`}>
                {postTypeLabel ? postTypeLabel(post.post_type) : post.post_type}
              </span>
              {post.archived ? (
                <span className="label-kicker text-[var(--brand-body)]">· Archived</span>
              ) : null}
              <span className="label-kicker text-[var(--brand-body)]">
                ·{" "}
                {new Date(post.display_at || post.created_at).toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
              </span>
            </div>
            <h3 className="mt-2 truncate font-display text-lg font-bold sm:text-xl">
              {post.title}
            </h3>
            {post.body || post.custom_intro ? (
              <CardIntro
                post={post}
                maxLen={220}
                plainTextOnly
                className="mt-1 line-clamp-2 text-sm text-[var(--brand-body)]"
                data-testid="archive-card-intro"
              />
            ) : null}
          </div>
        </div>
        <ArrowUpRight
          size={18}
          className="pointer-events-none relative z-[2] shrink-0 text-[var(--brand-body)]"
        />
      </div>
    </li>
  );
}
